import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { PinFlow } from './PinFlow';
import { setBiometricApi } from '../../lib/me';
import { runBiometric, biometricSupported } from '../../lib/biometric';
import { setLockFlag } from '../../lib/lock';
import keyIcon from '../../assets/figma/profile/key.svg';
import shieldIcon from '../../assets/figma/profile/shield.svg';

type Step = 'pinIntro' | 'pinEntry' | 'bioIntro';

/** Shown after first wallet create/restore. On web, PIN is required (server-side
 *  reauth protects seed-key and money moves); TMA can still skip. */
export function SecuritySetup({
  initData,
  onComplete,
  requirePin = false,
}: {
  initData: string;
  onComplete: () => void;
  /** When true, hide Skip on the PIN step (web). */
  requirePin?: boolean;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('pinIntro');
  const [busy, setBusy] = useState(false);

  if (step === 'pinEntry') {
    return (
      <PinFlow
        mode="set"
        initData={initData}
        onClose={() => setStep('pinIntro')}
        onDone={async () => {
          setLockFlag(true);
          // Only offer biometric when the client actually supports it (skips the
          // step entirely on Telegram Desktop instead of getting stuck).
          if (await biometricSupported()) setStep('bioIntro');
          else onComplete();
        }}
      />
    );
  }

  const enableBiometric = async () => {
    setBusy(true);
    try {
      const ok = await runBiometric(t('setup.bioReason'));
      if (ok) await setBiometricApi(initData, true).catch(() => {});
    } finally {
      setBusy(false);
      onComplete();
    }
  };

  const isPin = step === 'pinIntro';

  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col items-center px-6 pb-[max(2rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_4rem)] text-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-[28px] bg-white shadow-card">
          <img src={isPin ? keyIcon : shieldIcon} alt="" className="h-9 w-9" />
        </span>
        <h1 className="mt-6 text-[22px] font-semibold text-ink">
          {isPin ? t('setup.pinTitle') : t('setup.bioTitle')}
        </h1>
        <p className="mt-2 max-w-[18rem] text-[14px] leading-relaxed text-muted">
          {isPin ? t('setup.pinBody') : t('setup.bioBody')}
        </p>

        <div className="flex-1" />

        <button
          onClick={isPin ? () => setStep('pinEntry') : enableBiometric}
          disabled={busy}
          className="flex h-14 w-full max-w-sm items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-50"
        >
          {isPin ? t('setup.createPin') : busy ? '…' : t('setup.enableBiometric')}
        </button>
        {!(isPin && requirePin) && (
          <button
            onClick={onComplete}
            disabled={busy}
            className="mt-3 h-12 w-full max-w-sm text-[15px] font-medium text-muted active:opacity-70"
          >
            {t('setup.skip')}
          </button>
        )}
      </div>
    </div>
  );
}
